import { getShellHandoffUrl } from './shellHandoff.js';

/**
 * Identifier-first sign-in for the mobile shell (ERIN-8561). The generic
 * ERIN app hosts every company, so a fresh install cannot know whether the
 * person belongs to a company still on this app or to one that has moved to
 * the eAI portal. One box takes an email or phone number and this decides
 * where it goes:
 *
 *  - a phone number: this app has no phone login, so it goes to the shell's
 *    default portal, which signs people in by text message;
 *  - an email whose enterprise company carries the eAI flag: that company's
 *    portal, with the email carried so the portal continues on its own;
 *  - an email the enterprise app knows: the password field (and the
 *    company's SSO) as before;
 *  - an email the enterprise app does not know: the default portal, which
 *    either knows the person or says clearly that it does not.
 *
 * Pure apart from `lookupUser`, so the routing is unit-tested.
 */

export const classifyIdentifier = (raw) => {
	if (typeof raw !== 'string') return { kind: 'invalid' };
	const trimmed = raw.trim();
	if (!trimmed) return { kind: 'invalid' };
	if (trimmed.includes('@')) {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
			? { kind: 'email', value: trimmed.toLowerCase() }
			: { kind: 'invalid' };
	}
	if (/[A-Za-z]/.test(trimmed)) return { kind: 'invalid' };
	const digits = trimmed.replaceAll(/\D/g, '');
	if (digits.length < 10 || digits.length > 15) return { kind: 'invalid' };
	return { kind: 'phone', value: digits };
};

/**
 * @param {object} opts
 * @param {string} opts.identifier what the person typed
 * @param {(email: string) => Promise<object|null>} opts.lookupUser
 *   pre-login lookup by email (getLoginUser); resolves to the user with its
 *   company, or null
 * @param {string|null} opts.defaultPortalUrl the shell's portal for people
 *   this app does not know (null when the shell sent none)
 * @returns {Promise<
 *   | { next: 'portal', url: string, companyId?: string, email?: string, phone?: string }
 *   | { next: 'password', email: string }
 *   | { next: 'invalid' }
 *   | { next: 'unknown', email: string }
 * >}
 */
export const resolveShellSignIn = async ({
	identifier,
	lookupUser,
	defaultPortalUrl,
}) => {
	const id = classifyIdentifier(identifier);
	if (id.kind === 'invalid') return { next: 'invalid' };

	if (id.kind === 'phone') {
		return defaultPortalUrl
			? { next: 'portal', url: defaultPortalUrl, phone: id.value }
			: { next: 'invalid' };
	}

	let user = null;
	try {
		user = await lookupUser(id.value);
	} catch {
		// Lookup failed: fall through to the password form rather than guess.
		return { next: 'password', email: id.value };
	}

	if (user?.id) {
		const url = getShellHandoffUrl(user.company);
		if (url) {
			return {
				next: 'portal',
				url,
				companyId: user.companyId || user.company?.id,
				email: id.value,
			};
		}
		return { next: 'password', email: id.value };
	}

	return defaultPortalUrl
		? { next: 'portal', url: defaultPortalUrl, email: id.value }
		: { next: 'unknown', email: id.value };
};
